import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import {
    getOverPassUsage,
    blockOverPassUser,
    unblockOverPassUser,
    OverPassUsageRow,
} from "../../services/OverPassAdminService";
import Loading from "../../components/Loading";

const card: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(37,37,37,0.95) 100%)",
    border: "1px solid rgba(255,102,0,0.15)",
    borderRadius: 12,
    padding: "16px 20px",
    minWidth: 120,
    textAlign: "center",
};

const blockedBadge: React.CSSProperties = {
    background: "rgba(248,113,113,0.2)", color: "#f87171",
    borderRadius: 12, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
};
const okBadge: React.CSSProperties = {
    background: "rgba(74,222,128,0.2)", color: "#4ade80",
    borderRadius: 12, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
};

const fmtDate = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const AdminOverPassUsagePage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const isAdmin = !!user && admins.includes(user.steamID);

    const [rows, setRows] = useState<OverPassUsageRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState<string | null>(null); // steamId currently being toggled

    const load = () => {
        getOverPassUsage()
            .then(setRows)
            .catch(() => setError("Could not load OverPass usage."));
    };

    useEffect(() => {
        if (!isAdmin) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const toggleBlock = async (row: OverPassUsageRow) => {
        setBusy(row.steamId);
        try {
            if (row.blocked) {
                await unblockOverPassUser(row.steamId);
            } else {
                await blockOverPassUser(row.steamId);
            }
            // Optimistic flip, then refetch to stay authoritative.
            setRows(prev => prev?.map(r => r.steamId === row.steamId ? { ...r, blocked: !r.blocked } : r) ?? prev);
            load();
        } catch {
            setError(`Could not ${row.blocked ? "unblock" : "block"} ${row.personaName || row.steamId}.`);
        } finally {
            setBusy(null);
        }
    };

    const counts = useMemo(() => ({
        users: rows?.length ?? 0,
        opens: rows?.reduce((sum, r) => sum + (r.openCount || 0), 0) ?? 0,
        pulls: rows?.reduce((sum, r) => sum + (r.totalCalls || 0), 0) ?? 0,
        blocked: rows?.filter(r => r.blocked).length ?? 0,
    }), [rows]);

    const filtered = useMemo(() => {
        if (!rows) return [];
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.personaName?.toLowerCase().includes(q)) || r.steamId.includes(q));
    }, [rows, search]);

    if (!authLoaded) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;
    if (!isAdmin) return <div className="container mt-4"><div className="alert alert-danger">Admins only.</div></div>;
    if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    if (!rows) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;

    return (
        <div className="container mt-4" style={{ color: "var(--text-primary)" }}>
            <h1 style={{ color: "#e2e8f0", fontWeight: 700 }}>OverPass Usage</h1>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>
                Everyone who opens PowerHuds — how often they open it and how many matches they
                import. Block anyone you don't recognise or trust; blocked users can't open the app.
            </p>

            {/* Summary */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "20px 0" }}>
                {[
                    { label: "Users", value: counts.users, color: "#e2e8f0" },
                    { label: "App opens", value: counts.opens, color: "#60a5fa" },
                    { label: "Match pulls", value: counts.pulls, color: "#ff9933" },
                    { label: "Blocked", value: counts.blocked, color: "#f87171" },
                ].map(s => (
                    <div key={s.label} style={card}>
                        <div style={{ fontSize: "1.6rem", fontWeight: 700, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Controls */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
                <input
                    placeholder="Search name or SteamID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                        flex: "1 1 240px", maxWidth: 360,
                        background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,102,0,0.2)",
                        borderRadius: 6, color: "rgba(255,255,255,0.9)", padding: "8px 12px",
                    }}
                />
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>{filtered.length} shown</span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", background: "linear-gradient(135deg,#1a1a1a 0%,#252525 100%)", border: "1px solid rgba(255,102,0,0.1)", borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#e2e8f0" }}>
                    <thead style={{ background: "linear-gradient(135deg,#2a2a2a 0%,#333 100%)", borderBottom: "2px solid rgba(255,102,0,0.15)" }}>
                        <tr>
                            {["Player", "SteamID", "Opens", "Last Seen", "Pulls", "Last Pull", "Status", ""].map(h => (
                                <th key={h} style={{ padding: "12px 16px", textAlign: "left", textTransform: "uppercase", fontSize: 13, letterSpacing: "0.5px", color: "rgba(255,255,255,0.9)" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r, i) => (
                            <tr key={r.steamId} style={{ background: i % 2 ? "rgba(36,36,36,0.5)" : "rgba(42,42,42,0.5)" }}>
                                <td style={{ padding: "10px 16px" }}>
                                    <Link to={`/player/${r.steamId}`} style={{ color: "#ff9933", fontWeight: 600, textDecoration: "none" }}>
                                        {r.personaName || "—"}
                                    </Link>
                                </td>
                                <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{r.steamId}</td>
                                <td style={{ padding: "10px 16px", fontWeight: 600 }}>{r.openCount}</td>
                                <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{fmtDate(r.lastSeen)}</td>
                                <td style={{ padding: "10px 16px", fontWeight: 600 }}>{r.totalCalls}</td>
                                <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{fmtDate(r.lastCalledAt)}</td>
                                <td style={{ padding: "10px 16px" }}>
                                    {r.blocked
                                        ? <span style={blockedBadge} title={r.blockReason || undefined}>BLOCKED</span>
                                        : <span style={okBadge}>OK</span>}
                                </td>
                                <td style={{ padding: "10px 16px" }}>
                                    <button
                                        className={`btn btn-sm ${r.blocked ? "btn-outline-success" : "btn-outline-danger"}`}
                                        disabled={busy === r.steamId}
                                        onClick={() => toggleBlock(r)}
                                    >
                                        {busy === r.steamId ? "…" : r.blocked ? "Unblock" : "Block"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.5)" }}>No usage recorded yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminOverPassUsagePage;
